import { TF_PIXEL_EVENTS } from './tf-pixel-events';
import { buildTfPixelPayload } from './tf-pixel-payload';
import type { EcommerceItem } from './ecommerce-types';

declare global {
  interface Window {
    tfp?: (...args: unknown[]) => void;
  }
}

function invokeTfPixel(command: string, ...args: unknown[]): void {
  if (typeof window === 'undefined') return;
  if (typeof window.tfp !== 'function') return;
  window.tfp(command, ...args);
}

export function tfPixelTrack(eventName: string, payload: Record<string, unknown>): void {
  invokeTfPixel('track', eventName, payload);
}

export function tfPixelIdentify(payload: { email?: string; eventId: string; phone?: string }): void {
  invokeTfPixel('identify', { email: payload.email, event_id: payload.eventId, phone: payload.phone });
}

/** Manual page_view override — SDK auto-tracks on init and SPA navigation. */
export function tfPixelTrackPageView(payload: {
  eventId: string;
  page_path: string;
  page_title?: string;
}): void {
  tfPixelTrack(TF_PIXEL_EVENTS.PAGE_VIEW, {
    event_id: payload.eventId,
    page_path: payload.page_path,
    page_title: payload.page_title,
  });
}

export function tfPixelTrackViewContent(params: {
  currency: string;
  eventId: string;
  items: EcommerceItem[];
  value?: number;
}): void {
  tfPixelTrack(
    TF_PIXEL_EVENTS.PRODUCT_VIEW,
    {
      event_id: params.eventId,
      ...buildTfPixelPayload({
        currency: params.currency,
        items: params.items,
        value: params.value,
      }),
    },
  );
}

export function tfPixelTrackAddToCart(params: {
  currency: string;
  eventId: string;
  items: EcommerceItem[];
  value?: number;
}): void {
  tfPixelTrack(
    TF_PIXEL_EVENTS.ADD_TO_CART,
    {
      event_id: params.eventId,
      ...buildTfPixelPayload({
        currency: params.currency,
        items: params.items,
        value: params.value,
      }),
    },
  );
}

export function tfPixelTrackCheckoutStarted(params: {
  cartToken: string;
  coupon?: string;
  currency: string;
  eventId: string;
  itemCount?: number;
  items: EcommerceItem[];
  shipping?: number;
  tax?: number;
  value?: number;
}): void {
  tfPixelTrack(TF_PIXEL_EVENTS.CHECKOUT_STARTED, {
    cart_token: params.cartToken,
    event_id: params.eventId,
    ...(params.coupon ? { coupon: params.coupon } : {}),
    ...(params.itemCount != null ? { item_count: params.itemCount } : {}),
    ...(params.shipping != null ? { shipping: params.shipping } : {}),
    ...(params.tax != null ? { tax: params.tax } : {}),
    ...buildTfPixelPayload({
      currency: params.currency,
      items: params.items,
      value: params.value,
    }),
  });
}

export function tfPixelTrackPurchase(params: {
  affiliation?: string;
  coupon?: string;
  currency?: string;
  eventId: string;
  items?: EcommerceItem[];
  orderId: string;
  shipping?: number;
  tax?: number;
  value?: number;
}): void {
  tfPixelTrack(TF_PIXEL_EVENTS.PURCHASE, {
    event_id: params.eventId,
    ...(params.affiliation ? { affiliation: params.affiliation } : {}),
    ...(params.coupon ? { coupon: params.coupon } : {}),
    ...(params.shipping != null ? { shipping: params.shipping } : {}),
    ...(params.tax != null ? { tax: params.tax } : {}),
    ...buildTfPixelPayload({
      currency: params.currency,
      items: params.items,
      transactionId: params.orderId,
      value: params.value,
    }),
  });
}
